import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      // Endpoint always returns 200 to prevent enumeration; surface only network errors.
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md p-8 space-y-6 bg-card border border-border/50 rounded-lg shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="text-center">
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="forgot-title">RealProp</h1>
          <p className="text-muted-foreground mt-2">Reset your password</p>
        </div>

        {submitted ? (
          <div className="text-center space-y-4 py-6" data-testid="forgot-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600" />
            <p className="text-sm text-foreground">
              If an account exists for <strong>{email}</strong>, we've sent a password reset link.
            </p>
            <p className="text-xs text-muted-foreground">
              Check your inbox (and spam folder). The link expires in 1 hour.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline mt-4">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground text-center">
              Enter your email and we'll send you a link to reset your password.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 bg-secondary/20 border-transparent focus:border-primary rounded-md"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full h-12 bg-primary hover:bg-primary/90"
                data-testid="forgot-submit-button"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
            </form>
            <div className="text-center text-sm">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground" data-testid="back-to-login">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
